import { useState, useRef, useMemo, useCallback, useEffect } from 'react';

const DEFAULT_ANCHOR = { top: 88, right: 24 };
const DEFAULT_POSITION = { x: -200, y: 0 };

const useHudPanel = ({ initialCollapsed = true, initialPosition = DEFAULT_POSITION, anchor = DEFAULT_ANCHOR } = {}) => {
  const anchorConfig = useMemo(() => ({ ...DEFAULT_ANCHOR, ...(anchor || {}) }), [anchor]);
  const resolvedInitial = useMemo(() => initialPosition || DEFAULT_POSITION, [initialPosition?.x, initialPosition?.y]);

  const [hudCollapsed, setHudCollapsed] = useState(initialCollapsed);
  const [panelPosition, setPanelPosition] = useState(resolvedInitial);
  const pillDraggedRef = useRef(false);

  useEffect(() => {
    setPanelPosition((prev) => {
      if (prev?.x === resolvedInitial.x && prev?.y === resolvedInitial.y) return prev;
      return resolvedInitial;
    });
  }, [resolvedInitial.x, resolvedInitial.y]);

  const hudWrapperStyles = useMemo(
    () => ({
      position: 'fixed',
      ...(anchorConfig.top != null ? { top: anchorConfig.top } : {}),
      ...(anchorConfig.right != null ? { right: anchorConfig.right } : {}),
      ...(anchorConfig.left != null ? { left: anchorConfig.left } : {}),
      zIndex: (theme) => theme.zIndex.modal + 20,
      transition: 'opacity 200ms ease',
      opacity: hudCollapsed ? 0 : 1,
      visibility: hudCollapsed ? 'hidden' : 'visible',
      pointerEvents: hudCollapsed ? 'none' : 'auto'
    }),
    [anchorConfig, hudCollapsed]
  );

  const handleHudDrag = useCallback((_, data) => {
    setPanelPosition({ x: data.x, y: data.y });
  }, []);

  const handleHudStop = useCallback((_, data) => {
    setPanelPosition({ x: data.x, y: data.y });
  }, []);

  const collapseHud = useCallback(() => {
    setHudCollapsed(true);
  }, []);

  const expandHud = useCallback(() => {
    setHudCollapsed(false);
  }, []);

  const handlePillStart = useCallback(() => {
    pillDraggedRef.current = false;
  }, []);

  const handlePillDrag = useCallback((_, data) => {
    pillDraggedRef.current = true;
    setPanelPosition({ x: data.x, y: data.y });
  }, []);

  const handlePillStop = useCallback(() => {
    if (!pillDraggedRef.current) expandHud();
    pillDraggedRef.current = false;
  }, [expandHud]);

  const handlePillClick = useCallback(() => {
    if (!pillDraggedRef.current) expandHud();
  }, [expandHud]);

  const handlePillKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        expandHud();
      }
    },
    [expandHud]
  );

  return {
    hudCollapsed,
    hudPosition: panelPosition,
    hudWrapperStyles,
    hudDragHandlers: {
      handleDrag: handleHudDrag,
      handleStop: handleHudStop
    },
    setPanelPosition,
    collapseHud,
    expandHud,
    pillPosition: panelPosition,
    pillHandlers: {
      handleStart: handlePillStart,
      handleDrag: handlePillDrag,
      handleStop: handlePillStop,
      handleClick: handlePillClick,
      handleKeyDown: handlePillKeyDown
    },
    anchor: anchorConfig
  };
};

export default useHudPanel;
